import { params } from '../config.js';
import { airplaneState, keysPressed, respawnKeyUsed, setRespawnKeyUsed } from '../airplane/state.js';
import { startLaunchCharge, cancelLaunchCharge, restartAirplane } from '../airplane/update.js';
import { cycleCameraMode } from '../camera/modes.js';
import { updatePanelVisibility, togglePanel, pane } from '../ui/tweakpane.js';
import { pointMaterial } from '../terrain/material.js';

export function setupKeyboardControls() {
  window.addEventListener('keydown', (e) => {
    // Toggle dev panel with ~ key (Backquote)
    if (e.code === 'Backquote') {
      e.preventDefault();
      togglePanel();
      return;
    }

    if (!airplaneState.enabled) return;

    // Handle hold-to-launch when landed (W key)
    if (airplaneState.flightState === 'landed' && (e.code === 'ArrowUp' || e.code === 'KeyW')) {
      startLaunchCharge();
      e.preventDefault();
      return;
    }

    // Handle restart when crashed (R key)
    if (airplaneState.flightState === 'crashed' && e.code === 'KeyR') {
      restartAirplane();
      setRespawnKeyUsed(true); // Prevent keyup from toggling ground highlight
      e.preventDefault();
      return;
    }

    // Disable regular controls when not flying
    if (airplaneState.flightState !== 'flying') return;

    if (e.code === 'ArrowUp' || e.code === 'KeyW') {
      keysPressed.throttleUp = true;
      e.preventDefault();
    }
    if (e.code === 'ArrowDown' || e.code === 'KeyS') {
      keysPressed.throttleDown = true;
      e.preventDefault();
    }
    if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
      keysPressed.strafeLeft = true;
      e.preventDefault();
    }
    if (e.code === 'ArrowRight' || e.code === 'KeyD') {
      keysPressed.strafeRight = true;
      e.preventDefault();
    }
    // Handle shift for boost - instant activation on press
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
      // Only set justPressed if not already holding shift (prevents repeat triggers)
      if (!airplaneState.isShiftPressed) {
        airplaneState.shiftJustPressed = true;
      }
      airplaneState.isShiftPressed = true;
    }
  });

  window.addEventListener('keyup', (e) => {
    if (e.code === 'ArrowUp' || e.code === 'KeyW') {
      keysPressed.throttleUp = false;
      // Cancel launch charge if released early
      if (airplaneState.launchCharging) {
        cancelLaunchCharge();
      }
    }
    if (e.code === 'ArrowDown' || e.code === 'KeyS') {
      keysPressed.throttleDown = false;
    }
    if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
      keysPressed.strafeLeft = false;
    }
    if (e.code === 'ArrowRight' || e.code === 'KeyD') {
      keysPressed.strafeRight = false;
    }
    // G key cycles camera modes
    if (e.code === 'KeyG') {
      cycleCameraMode();
    }
    // R key cycles through three states:
    // 0: Default (everything off)
    // 1: Airplane ground highlight on (no permanent color)
    // 2: Permanent height-based color on (NO airplane highlight)
    if (e.code === 'KeyR') {
      if (respawnKeyUsed) {
        setRespawnKeyUsed(false); // Reset flag, skip toggle this time
      } else {
        // Cycle state 0 -> 1 -> 2 -> 0
        params.rKeyState = (params.rKeyState + 1) % 3;

        // Update airplane highlight: ONLY in state 1
        params.airplaneHoverEnabled = (params.rKeyState === 1);
        pointMaterial.uniforms.uAirplaneHoverEnabled.value = params.airplaneHoverEnabled ? 1.0 : 0.0;

        // Update radar/ring color based on state
        // Permanent color is ON for state 2
        const isPermanent = (params.rKeyState === 2);
        pointMaterial.uniforms.uRadarShowRing.value = isPermanent ? 1.0 : 0.0;
        pointMaterial.uniforms.uRadarPermanent.value = isPermanent ? 1.0 : 0.0;

        if (pane) pane.refresh();
      }
    }
    // Handle shift for boost
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
      airplaneState.isShiftPressed = false;
    }
  });
}
